import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FixedSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  useTable,
  useFlexLayout,
  useSortBy,
  useRowSelect,
  useFilters,
} from "react-table";
import { useSticky } from "react-table-sticky";
import { KInfoTooltip } from "@kyburz/kdesign-system";

import { usePrevious } from "../../hooks/usePrevious";

import TableCell from "./TableCell/TableCell";
import ColumnsSorter from "./ColumnSorter/ColumnsSorter";
import InlineColumnFilter from "./ColumnFilter/InlineColumnFilter";

import {
  TableCheckbox,
  TableCheckboxProps,
} from "./TableCheckbox/TableCheckbox";

import { useStyles } from "./tableStyles";
import FilterField from "./ColumnFilter/FilterField";
import Placeholder from "./Placeholder";
import { useRemValue } from "../../hooks/useBaseFontSize";

interface SelectionColumnProps {
  sticky?: "left" | "right";
}

interface Props {
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  loadNextPage?: any;
  columns: any;
  data: any;
  updateMyData?: () => void;
  includedInInlineEditing?: string[] | null;
  forceReload?: boolean;
  manualSortBy?: boolean;
  manualFilters?: boolean;
  manualRowSelectedKey?: string;
  filterValue?: { id: string; value: string }[];
  sortTable?: (sortBy: any) => void;
  filterTable?: (filterBy: any) => void;
  initialSortBy?: Array<any>;
  tableHeight?: string;
  itemSize?: number;
  onSelectRows?:
    | ((isAll: boolean, ids: Record<string, boolean>) => void)
    | null;
  onControlledSelect?: (row: Record<string, any>, checked: boolean) => void;
  onControlledSelectAll?: (data: any[], checked: boolean) => void;
  initialSelectedRows?: Record<string, boolean>;
  autoResetSelectedRows?: boolean;
  hasFilter?: boolean;
  hasStickyColumns?: boolean;
  loading?: boolean;
  rootClassName?: string;
  selectionColumnProps?: SelectionColumnProps;
  noDataLabel?: string;
  noSearchLabel?: string;
  onRowClick?: (row: Record<string, any>) => void;
  highlightedRowIndex?: number;
}

const KInfiniteTable = ({
  hasNextPage,
  isNextPageLoading,
  loadNextPage = () => {},
  columns,
  data,
  manualRowSelectedKey,
  updateMyData = () => {},
  includedInInlineEditing = null,
  manualSortBy = false,
  manualFilters = false,
  sortTable = () => {},
  filterTable = () => {},
  filterValue = [],
  initialSortBy = [],
  tableHeight = "100%",
  itemSize = 40,
  onSelectRows = null,
  onControlledSelect,
  onControlledSelectAll,
  initialSelectedRows = {},
  autoResetSelectedRows = false,
  hasFilter = false,
  hasStickyColumns = false,
  loading = false,
  rootClassName = "",
  selectionColumnProps = {},
  noDataLabel = "No data to display",
  noSearchLabel = "No search results",
  onRowClick = () => {},
  highlightedRowIndex,
}: Props) => {
  const [activeFilter, setActiveFilter] = useState(null);
  const classes = useStyles({ height: tableHeight, hasStickyColumns });
  const itemCount = hasNextPage ? data?.length + 1 : data?.length;
  const loadMoreItems = isNextPageLoading ? () => {} : loadNextPage;
  const isItemLoaded = (index: number) => !hasNextPage || index < data?.length;

  const defaultColumn = { Cell: TableCell };

  const itemSizeRem = useRemValue(itemSize);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    isAllRowsSelected,
    toggleAllRowsSelected,
    toggleRowSelected,
    totalColumnsWidth,
    state: { sortBy, selectedRowIds, filters },
    setFilter,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      updateMyData,
      includedInInlineEditing,
      manualSortBy,
      manualFilters,
      manualRowSelectedKey,
      initialState: {
        filters: filterValue,
        sortBy: initialSortBy,
        selectedRowIds: initialSelectedRows,
      },
      autoResetSelectedRows,
    },
    useSticky,
    useFilters,
    useFlexLayout,
    useSortBy,
    useRowSelect,
    (hooks) => {
      return onSelectRows || onControlledSelect
        ? hooks.visibleColumns.push((visibleColumns) => [
            {
              id: "selection",
              width: 50,
              Header: ({ getToggleAllRowsSelectedProps, data: initData }) => {
                const props = onControlledSelectAll
                  ? {
                      onChange: (e: any) =>
                        onControlledSelectAll(initData, e.target.checked),
                    }
                  : {};
                return (
                  <TableCheckbox
                    {...(getToggleAllRowsSelectedProps(
                      props,
                    ) as TableCheckboxProps)}
                  />
                );
              },
              Cell: ({ row }) => {
                const props = onControlledSelect
                  ? {
                      onChange: (e: any) =>
                        onControlledSelect(row, e.target.checked),
                    }
                  : {};
                return (
                  <TableCheckbox
                    {...(row.getToggleRowSelectedProps(
                      props,
                    ) as TableCheckboxProps)}
                  />
                );
              },
              ...selectionColumnProps,
            },
            ...visibleColumns,
          ])
        : null;
    },
  );

  const isEmptyData = useMemo(() => {
    return rows?.length === 0 ? noDataLabel : null;
  }, [rows]);

  const isEmptySearch = useMemo(() => {
    return filters.length !== 0 ? noSearchLabel : null;
  }, [filters]);

  const prevIsAllRowsSelected = usePrevious(isAllRowsSelected);
  useEffect(() => {
    if (prevIsAllRowsSelected) toggleAllRowsSelected(true);
  }, [data]);

  useEffect(() => {
    toggleAllRowsSelected(false);
    sortTable(sortBy);
  }, [JSON.stringify(sortBy)]);

  useEffect(() => {
    filterTable(filters);
  }, [filters]);

  // reset filter when column becomes hidden
  useEffect(() => {
    if (manualFilters && filters.length) {
      const filterValueIds = filterValue.map(({ id }) => id);
      filters
        .filter(
          ({ id }) =>
            columns.findIndex((item: any) => item?.id && item.id === id) >= 0,
        )
        .forEach(({ id }) => {
          if (!filterValueIds.includes(id)) setFilter(id, "");
        });
    }
  }, [columns?.length]);

  useEffect(() => {
    if (onSelectRows) {
      onSelectRows(isAllRowsSelected, selectedRowIds);
    }
  }, [JSON.stringify(selectedRowIds)]);

  useEffect(() => {
    if (!manualRowSelectedKey) return;
    rows.forEach(({ id, original }: any) => {
      if (manualRowSelectedKey in original) {
        toggleRowSelected(id, original[manualRowSelectedKey]);
      }
    });
  }, [rows]);

  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const row = rows[index];
      if (row === undefined) return null;
      prepareRow(row);
      let content;
      if (!rows[index]) {
        content = <div style={style}>Loading...</div>;
      } else {
        content = (
          <div
            onClick={() => onRowClick(row)}
            {...row.getRowProps({ style })}
            className={clsx(classes.tr, {
              [classes.trLoading]: loading,
              [classes.trHighlightedRow]:
                highlightedRowIndex != null && index === highlightedRowIndex,
            })}
          >
            {row.cells.map((cell) => {
              const className = clsx(classes.td, {
                [classes.tdActiveFilter]:
                  cell?.column?.id && cell?.column?.id === activeFilter,
              });
              return (
                <div {...cell.getCellProps()} className={className}>
                  {cell.render("Cell")}
                </div>
              );
            })}
          </div>
        );
      }
      return content;
    },
    [prepareRow, rows],
  );

  const renderColumnHeader = (column: any) => (
    <>
      <div className={classes.thContent}>
        {column.defaultCanSort && (
          <ColumnsSorter
            isSorted={column.isSorted}
            isSortedDesc={column.isSortedDesc}
          />
        )}
        {column.render("Header")}
      </div>
      {hasFilter && column.canFilter && (
        <InlineColumnFilter column={column} setActiveFilter={setActiveFilter} />
      )}
      {activeFilter && column.id === activeFilter && (
        <FilterField column={column} setActiveFilter={setActiveFilter} />
      )}
    </>
  );

  const RenderHeader = (headerGroup: any) => {
    return (
      <div
        {...headerGroup.getHeaderGroupProps()}
        className={classes.tr}
        key={headerGroup.id}
      >
        {headerGroup.headers.map((column: any) => {
          const props = {
            ...column.getHeaderProps(
              column.defaultCanSort &&
                column.getSortByToggleProps({ title: null }),
            ),
            className: clsx(classes.th, {
              [classes.thSeparator]: !["selection", "actions"].includes(
                column.id,
              ),
              [classes.thHover]: column.defaultCanFilter || column.canFilter,
              [classes.thActiveFilter]: column.id === activeFilter,
            }),
          };
          return column?.tooltip ? (
            <KInfoTooltip {...props} title={column?.tooltip} placement="bottom">
              {renderColumnHeader(column)}
            </KInfoTooltip>
          ) : (
            <div {...props}>{renderColumnHeader(column)}</div>
          );
        })}
      </div>
    );
  };

  const renderList = React.useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      React.forwardRef((props: any, ref: any) => (
        <>
          <div
            style={hasStickyColumns ? { width: totalColumnsWidth } : {}}
            className={classes.thead}
          >
            {RenderHeader(headerGroups[0])}
          </div>
          {(!isEmptyData || !isEmptySearch) && (
            <div
              {...getTableBodyProps({
                className: clsx({
                  [classes.tbodyInfinite]: !hasFilter,
                  [classes.tbodyInfinite_filter]: hasFilter,
                }),
              })}
              style={
                hasStickyColumns
                  ? { ...props.style, width: totalColumnsWidth }
                  : props.style
              }
            >
              {props.children}
            </div>
          )}
        </>
      )),
    [columns, classes, activeFilter, isEmptyData, isEmptySearch],
  );

  const tableClassName = clsx(
    classes.table,
    {
      [classes.sticky]: hasStickyColumns,
    },
    isEmptyData && classes.tableNoData,
  );

  return (
    <div className={clsx(classes.root, rootClassName)}>
      <div
        {...getTableProps()}
        style={hasStickyColumns ? { minWidth: "unset" } : {}}
        className={tableClassName}
      >
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
        >
          {({ onItemsRendered, ref }) => (
            <AutoSizer>
              {({ height, width }: { height: number; width?: number }) => (
                <FixedSizeList
                  className={classes.fixedSizeList}
                  height={height}
                  width={width}
                  itemCount={itemCount}
                  itemSize={itemSizeRem}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  overscanCount={0}
                  innerElementType={renderList}
                >
                  {RenderRow}
                </FixedSizeList>
              )}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </div>
      {isEmptyData && !isEmptySearch && (
        <Placeholder>{isEmptyData}</Placeholder>
      )}
      {isEmptySearch && <Placeholder>{isEmptySearch}</Placeholder>}
    </div>
  );
};

export default KInfiniteTable;
